import { useState } from 'react';
import { ApiError } from '../lib/api';
import { formatWhen } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useSetPromised } from '../lib/queries';
import { useApiError } from '../lib/useApiError';
import { DueEditor } from './DueEditor';
import { Sheet } from './Sheet';
import { Alert } from './ui';

/** Minutes left, or past, against what the customer was told — and, for whoever takes cars in, a tap to move it. */
export function Deadline({ orderId, promisedAt, mayEdit }: { orderId: string; promisedAt: string | null; mayEdit: boolean }) {
  const { t } = useI18n();
  const errorText = useApiError();
  const save = useSetPromised(orderId);
  const [open, setOpen] = useState(false);

  const due = promisedAt ? new Date(promisedAt) : null;
  const late = due !== null && due < new Date();
  const label = due
    ? (late ? t('floor.overdueBy', { time: formatWhen(due) }) : t('floor.promisedAt', { time: formatWhen(due) }))
    : t('floor.setPromise');
  const start = () => {
    save.reset();
    setOpen(true);
  };
  const tone = late ? 'font-semibold text-danger' : due ? 'text-ink-faint' : 'text-brand';

  if (!mayEdit) return due ? <span className={`tabular text-xs ${tone}`}>{label}</span> : null;
  return (
    <>
      <button
        onClick={start}
        className={`tabular inline-flex min-h-9 items-center text-sm font-semibold underline decoration-dotted underline-offset-4 ${
          late ? 'text-danger' : 'text-brand'
        }`}
      >
        {label}
      </button>
      {open && (
        <Sheet open onClose={() => setOpen(false)} title={t('floor.promise')}>
          {save.error instanceof ApiError && <div className="mb-3"><Alert>{errorText(save.error) ?? save.error.message}</Alert></div>}
          <DueEditor
            initial={due}
            saving={save.isPending && save.variables !== null}
            clearing={save.isPending && save.variables === null}
            onSave={(when) => save.mutate(when.toISOString(), { onSuccess: () => setOpen(false) })}
            onClear={due ? () => save.mutate(null, { onSuccess: () => setOpen(false) }) : null}
          />
        </Sheet>
      )}
    </>
  );
}
